// SubbieRectifyModal — "Mark rectified" form for a subbie's defect.
//
// v0.43.1 — initial. Opened from MyItemsScreen when a subbie taps one of
//           their assigned defects. Shows the defect context (description,
//           location, drawing name) + a comment textarea + a single
//           photo capture/upload + Submit. POSTs to
//           /api/contact-defect-rectify; on success, calls
//           onRectified(updatedDefect) so the parent can refresh.
//
// The photo input uses capture="environment" on mobile so it opens the
// v0.49.0 — signed contact session: contactId no longer sent in the
//           body (and no longer accepted as a prop). The server derives
//           identity from the httpOnly droppin_contact cookie set at
//           /invite/<token> login. A 401 here means the session
//           expired — the error message tells the subbie to ask for a
//           new invite link.
// rear camera by default — most rectification evidence is shot on-site.
//
// Photo handling is the same data-URL pattern used by the existing
// admin defect uploader: small files only, server stores the data URL
// on DefectPhotos.Url. Cap at 2MB so we don't accidentally POST a 12MP
// camera capture.

import React, { useState } from 'react';
// 2026-09-15 — iPhone photos (HEIC) become JPEG before they are read.
import { toUploadableImage } from '../utils/heicPhotos';
import { apiSend } from '../api';
import { theme, btnPrimary, btnSecondary, input, card } from '../theme';
// v0.50.0 — lucide icon replaces emoji glyph.
import { MapPin } from 'lucide-react';
import { makeImageVariants } from '../utils/imageVariants';
// v0.328.0 - the ghost camera: the item's original photo faded over the live
// view, so the fix is shot from where the defect was.
import GhostCamera from './GhostCamera';

const PHOTO_CAP_BYTES = 2 * 1024 * 1024; // 2MB
// v0.328.0 - the ghost camera's shot is asked at this size (above the 1600 px
// copy the PDF uses), and a phone-camera fallback shot over the cap is scaled
// down to it rather than refused: the subbie did nothing wrong.
const GHOST_MAX_EDGE = 1800;

async function shrinkUnderCap(file) {
  if (!file || file.size <= PHOTO_CAP_BYTES) return file;
  const bmp = await createImageBitmap(file);
  try {
    for (const edge of [GHOST_MAX_EDGE, 1400, 1100]) {
      const scale = Math.min(1, edge / Math.max(bmp.width, bmp.height));
      const c = document.createElement('canvas');
      c.width = Math.round(bmp.width * scale);
      c.height = Math.round(bmp.height * scale);
      c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
      const blob = await new Promise((res) => c.toBlob(res, 'image/jpeg', 0.85));
      if (blob && blob.size <= PHOTO_CAP_BYTES) return new File([blob], file.name || 'after.jpg', { type: 'image/jpeg' });
    }
  } finally { if (bmp.close) bmp.close(); }
  return file; // still over: onPhotoFile says so
}

export default function SubbieRectifyModal({ defect, onCancel, onRectified }) {
  const [comment, setComment] = useState('');
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  // v0.328.0 - the photo the ghost camera lays over: the item's first own photo
  // (the server picks it, api/contact-defects). None = the ordinary photo box.
  const ghost = defect && defect.beforePhoto && defect.beforePhoto.id != null && defect.beforePhoto.url ? defect.beforePhoto : null;
  // The camera opens by itself when there is an original to line up with;
  // closing it leaves the form, with "Take the after photo" to reopen it.
  const [camOpen, setCamOpen] = useState(!!ghost);
  // The before photo the attached photo answers - set only by the ghost
  // camera, cleared by a library pick or Remove photo.
  const [afterOf, setAfterOf] = useState(null);

  async function onGhostShot(file) {
    setCamOpen(false);
    let small;
    try { small = await shrinkUnderCap(file); } catch { small = file; }
    const ok = await onPhotoFile(small);
    if (ok) setAfterOf(ghost.id);
  }

  async function onPhotoFile(picked) {
    setErr(null);
    setAfterOf(null);
    if (!picked) { setPhotoDataUrl(null); return false; }
    // 2026-09-15 — an iPhone photo (HEIC) is converted to a JPEG, at the stored
    // size, before the size check and the read (utils/heicPhotos).
    let file;
    try { file = await toUploadableImage(picked); } catch (convErr) { setErr(convErr?.message || 'Could not read the photo.'); return false; }
    if (file.size > PHOTO_CAP_BYTES) {
      setErr(`Photo is over 2MB. Use a smaller image (this is ${Math.round(file.size / 1024)} KB).`);
      return false;
    }
    try {
      const url = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error('Could not read the photo.'));
        r.readAsDataURL(file);
      });
      setPhotoDataUrl(url);
      return true;
    } catch (e) {
      setErr(e?.message || String(e));
      return false;
    }
  }

  async function submit() {
    setErr(null);
    if (!comment.trim()) {
      setErr('Add a short note on what you did.');
      return;
    }
    setSubmitting(true);
    try {
      // v0.103.0 - downscaled copies of the evidence photo, so a subbie's
      // rectification carries the same variants a staff capture does. Without
      // this every post-handover photo went back to being a camera original.
      const variants = await makeImageVariants(photoDataUrl);
      const res = await apiSend('POST', '/api/contact-defect-rectify', {
        defectId: defect.id,
        comment: comment.trim(),
        photoDataUrl,
        photoThumbnailUrl: variants.thumbnailUrl,
        photoDisplayUrl: variants.displayUrl,
        // v0.328.0 - names the before only when the ghost camera took it.
        beforePhotoId: afterOf || undefined,
      });
      if (typeof onRectified === 'function') onRectified(res?.defect || null);
    } catch (e) {
      setErr(e?.message || String(e));
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={() => !submitting && onCancel()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 14,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, width: 480, maxWidth: '100%', maxHeight: '90vh',
          borderRadius: 12, padding: 18, overflow: 'auto',
          boxShadow: '0 12px 32px rgba(15,23,42,0.25)',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Mark rectified</h3>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{ border: 0, background: 'transparent', fontSize: 20, color: theme.textLight, cursor: 'pointer', lineHeight: 1 }}
          >×</button>
        </div>

        {/* Defect summary so the subbie confirms they're rectifying the right item. */}
        <div style={{ ...card, padding: 10, marginBottom: 12, background: theme.bg }}>
          <div style={{ fontSize: 11, color: theme.textLight, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {defect.projectName}
            {defect.drawingName && <> · {defect.drawingName}</>}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, marginTop: 4 }}>
            {defect.defectNumber != null && <span style={{ color: theme.textLight, fontWeight: 500, marginRight: 6 }}>#{defect.defectNumber}</span>}
            {defect.description || '(no description)'}
          </div>
          {defect.locationLabel && (
            <div style={{ fontSize: 12, color: theme.textLight, marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={11} />{defect.locationLabel}</div>
          )}
        </div>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.textLight, textTransform: 'uppercase', marginBottom: 4 }}>
            What you did <span style={{ color: theme.danger }}>*</span>
          </div>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Replaced cracked tile, regrouted, sealed edge."
            style={{ ...input, width: '100%', height: 80, resize: 'vertical', fontFamily: 'inherit' }}
            maxLength={2000}
          />
        </label>

        {/* A group, not a <label>: a label names its FIRST control, which since
            v0.328 is the Take the after photo button - a tap on the photo or the
            words would open the camera. */}
        <div role="group" aria-label="Evidence photo (optional)" style={{ display: 'block', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.textLight, textTransform: 'uppercase', marginBottom: 4 }}>
            Evidence photo (optional)
          </div>
          {ghost && (
            <div style={{ marginBottom: 8 }}>
              <button type="button" onClick={() => setCamOpen(true)} disabled={submitting} style={btnSecondary}>
                Take the after photo
              </button>
              <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4 }}>
                The camera shows the original photo faded over the view, so the fix is taken from the same spot.
              </div>
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={e => onPhotoFile(e.target.files?.[0])}
            aria-label="Evidence photo"
            style={{ fontSize: 13 }}
          />
          {photoDataUrl && (
            <div style={{ marginTop: 8 }}>
              {afterOf && (
                <div style={{ fontSize: 11, fontWeight: 700, color: theme.success, textTransform: 'uppercase', marginBottom: 4 }}>
                  After photo - shown beside the original
                </div>
              )}
              <img
                src={photoDataUrl}
                alt=""
                style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6, border: `1px solid ${theme.border}` }}
              />
              <div>
                <button
                  type="button"
                  onClick={() => { setPhotoDataUrl(null); setAfterOf(null); }}
                  style={{
                    marginTop: 4, fontSize: 12, padding: '4px 10px',
                    border: 0, background: 'transparent', color: theme.danger,
                    cursor: 'pointer', fontWeight: 600,
                  }}
                >Remove photo</button>
              </div>
            </div>
          )}
        </div>

        {err && (
          <div style={{
            padding: '8px 10px', marginBottom: 10, fontSize: 13,
            background: theme.bg, border: `1px solid ${theme.danger}`,
            color: theme.danger, borderRadius: 6, fontWeight: 600,
          }}>{err}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} disabled={submitting} style={btnSecondary}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            style={{ ...btnPrimary, opacity: submitting ? 0.5 : 1 }}
          >{submitting ? 'Submitting…' : 'Mark rectified'}</button>
        </div>
      </div>
      {camOpen && ghost && (
        <GhostCamera
          beforePhoto={ghost}
          maxEdge={GHOST_MAX_EDGE}
          onUse={onGhostShot}
          onClose={() => setCamOpen(false)}
        />
      )}
    </div>
  );
}
